// v3 业务字段 → 数据访问器映射
//
// 数据源：docs/modules/robot-manager/business-analysis/v3-fields.json (82 字段)
// 此文件桥接「业务字段名（v3 master mapping）」与「v3 数据模型 path」。
//
// 已知能取值的字段（accessor 返回非空）：~22 个 —— 这些是 v3 当前已落地的字段。
// 暂未落地的字段：accessor 返回 null/undefined —— UI 仍列出，fallback "—"。
//
// 渐进补全：backend metadata schema 落实哪个字段，本文件加对应 accessor 即可，UI 不动。

import type { RobotUnit, RobotLifecycleStage } from './api';

export type V3FieldKey = string; // 即 v3-fields.json 里的 field 名（业务术语）

export type RenderType = 'text' | 'number' | 'money' | 'date' | 'datetime' | 'enum' | 'badge' | 'url' | 'fk';

/** 写回路径：column = RobotUnit 直接字段 / metadata = metadata Json patch */
export interface WritePath {
  kind: 'column' | 'metadata';
  /** column 类是 backend dto 字段名（如 'supplierSn'）；metadata 类是 metadata Json key */
  key: string;
}

export interface V3FieldDef {
  /** 业务字段名（v3 master mapping，作为 ColumnConfig key 用） */
  field: V3FieldKey;
  /** 中文 label */
  cnLabel: string;
  /** 英文 label */
  enLabel: string;
  /** v3 主写节点编号 */
  mainNode: string;
  /** 数据访问器：返回 null/undefined → 显示 "—" */
  accessor?: (robot: RobotUnit) => any;
  /** 渲染类型 */
  renderType?: RenderType;
  /** 写回路径；undefined → read-only（编辑控件 disabled） */
  writePath?: WritePath;
  /** Enum 选项（renderType=enum） */
  enumOptions?: { value: string; label: string }[];
  /** 字典 category（如 'tariff_type'）—— 从字典 API 拉 options，与 enumOptions 二选一 */
  dictCategory?: string;
  /** FK 资源（renderType=fk）—— 决定下拉拉哪个 list API */
  fkResource?: 'customer' | 'supplier' | 'location' | 'sku' | 'model';
  /** 默认是否在列表显示（仅作为初次默认；用户偏好覆盖） */
  defaultVisible?: boolean;
}

/** v3 字段名 → camelCase metadata key（用于盲写场景的默认 key 规范化） */
export function normalizeFieldKey(field: string): string {
  return field
    .replace(/[()\/]/g, ' ')
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((p, i) => (i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
    .join('');
}

// 工具：取链式属性，缺失返回 undefined（不抛错）
const g = (obj: any, ...keys: string[]): any => {
  let cur = obj;
  for (const k of keys) {
    if (cur == null) return undefined;
    cur = cur[k];
  }
  return cur;
};

/** v3 业务字段映射表（82 entries）
 *
 * writePath 规则：
 * - kind='column'：写到 RobotUnit 表直接字段（backend UpdateRobotUnitDto 已支持的 6 个：
 *   supplierSn / usageType / manufactureDate / sapMaterialNo / disposalType / disposalNotes）
 * - kind='metadata'：写到 metadata Json，key 用 normalizeFieldKey(field) 规范化
 * - undefined：read-only（如 ffsn / model / sku / PO 来自关联表，不可在此面板直接改）
 */
export const V3_FIELDS: V3FieldDef[] = [
  // ─── 节点 01 PO 创建 ───
  { field: 'FF SN (key)',           cnLabel: 'FF 内部序列号',        enLabel: 'FF SN',                 mainNode: '01', accessor: (r) => g(r, 'ffsn'),                        renderType: 'text', defaultVisible: true /* read-only：系统生成 */ },
  /* 已删：'FF SN'（跟 FF SN (key) 重复显示）、'Placeholder SN (orig)'（业务方日常不需查看激活历史） */
  { field: 'Model (full)',          cnLabel: '型号',                 enLabel: 'Model (full)',          mainNode: '01', accessor: (r) => g(r, 'model', 'name') ?? g(r, 'model', 'code'), renderType: 'fk', fkResource: 'model', defaultVisible: true /* read-only：占位激活后不应改 */ },
  { field: 'Variant (aux)',         cnLabel: '型号变体',             enLabel: 'Variant (aux)',         mainNode: '01', accessor: (r) => g(r, 'sku', 'code'),                 renderType: 'fk', fkResource: 'sku' /* read-only：同上 */ },
  { field: 'Usage Type',            cnLabel: '用途分类',             enLabel: 'Usage Type',            mainNode: '01', accessor: (r) => g(r, 'usageType'),                   renderType: 'enum', writePath: { kind: 'column', key: 'usageType' },
    enumOptions: [
      { value: 'SALES', label: '销售' },
      { value: 'RND', label: '研发样机' },
      { value: 'MARKETING', label: '市场展示' },
      { value: 'CAPITAL', label: '资本' },
      { value: 'PRODUCT', label: '产品' },
      { value: 'TO_AGIBOT', label: 'AGIBOT 调拨' },
      { value: 'LAUNCH_EVENT', label: '发布活动' },
    ] },
  { field: 'PO (merged)',           cnLabel: '采购单号(PO)',         enLabel: 'PO (merged)',           mainNode: '01', accessor: (r) => g(r, 'purchaseOrder', 'poNo'),       renderType: 'text' /* read-only：来自 PO 表 */ },
  { field: 'Purchase Date',         cnLabel: '采购日期',             enLabel: 'Purchase Date',         mainNode: '01', accessor: (r) => g(r, 'purchaseOrder', 'orderedAt'),  renderType: 'date' /* read-only：来自 PO 表 */ },
  { field: 'Partner / Supplier',    cnLabel: '采购合作方',           enLabel: 'Partner / Supplier',    mainNode: '01',
    /* supplier 跨 schema 无 Prisma @relation —— my-work 在 fetch 时 enrich 注入 r.purchaseOrder.supplier = { name } */
    accessor: (r) => g(r, 'purchaseOrder', 'supplier', 'name') ?? g(r, 'supplier', 'name'),
    renderType: 'fk', fkResource: 'supplier', defaultVisible: true /* read-only：来自 PO 表 */ },
  { field: 'Purchase Price',        cnLabel: '采购价',               enLabel: 'Purchase Price',        mainNode: '01', accessor: (r) => g(r, 'purchaseOrderLine', 'unitPrice'), renderType: 'money' /* read-only：来自 PO Line */ },

  // ─── 节点 03 G1/G2 工厂出货 ───
  { field: 'sticker',               cnLabel: '合规贴纸',             enLabel: 'sticker',               mainNode: '03', renderType: 'text' },
  { field: 'Date ready',            cnLabel: '可售日期',             enLabel: 'Date ready',            mainNode: '03', renderType: 'date' },

  // ─── 节点 04 国际运输 ───
  { field: 'Arrival Date',          cnLabel: '到货日期',             enLabel: 'Arrival Date',          mainNode: '04', renderType: 'date' },
  { field: 'Logistics Status',      cnLabel: '物流状态',             enLabel: 'Logistics Status',      mainNode: '04', renderType: 'enum' },
  { field: "Add'l Notes (Shipping)", cnLabel: '运输备注',            enLabel: "Add'l Notes (Shipping)", mainNode: '04', renderType: 'text' },
  { field: 'Import Declaration Type', cnLabel: '进口申报类型',        enLabel: 'Import Declaration Type', mainNode: '04', renderType: 'enum', dictCategory: 'declaration_type' },
  { field: 'Tariff Type',           cnLabel: '关税类型',             enLabel: 'Tariff Type',           mainNode: '04', renderType: 'enum', dictCategory: 'tariff_type' },
  { field: 'Shipping Receipts',     cnLabel: '运输收据链接',          enLabel: 'Shipping Receipts',     mainNode: '04', renderType: 'url' },

  // ─── 节点 05 保税 ───
  { field: 'Compliance Notes',      cnLabel: '合规备注',             enLabel: 'Compliance Notes',      mainNode: '05', renderType: 'text' },

  // ─── 节点 06 清关 ───
  { field: 'FCC Status',            cnLabel: 'FCC 状态',             enLabel: 'FCC Status',            mainNode: '06', renderType: 'enum' },

  // ─── 节点 07 美国收货激活 ───
  { field: 'Supplier SN',           cnLabel: '供应商序列号',          enLabel: 'Supplier SN',           mainNode: '07', accessor: (r) => g(r, 'supplierSn'),                  renderType: 'text', writePath: { kind: 'column', key: 'supplierSn' } },

  // ─── 节点 08 W1 PDI ───
  { field: 'Issue',                 cnLabel: '问题(Issue)',          enLabel: 'Issue',                 mainNode: '08', renderType: 'text' },
  { field: 'Issue Tag',             cnLabel: '问题标签',             enLabel: 'Issue Tag',             mainNode: '08', renderType: 'enum', dictCategory: 'service_issue_type' },

  // ─── 节点 09 标签贴附 ───
  { field: 'Label, body - FCC',     cnLabel: '机身 FCC 标',          enLabel: 'Label, body - FCC',     mainNode: '09', renderType: 'enum' },
  { field: 'Label, body Made in CN', cnLabel: '机身 Made in CN 标',   enLabel: 'Label, body Made in CN', mainNode: '09', renderType: 'enum' },
  { field: 'Label, SN Body',        cnLabel: '机身 SN 标',           enLabel: 'Label, SN Body',        mainNode: '09', renderType: 'enum' },
  { field: 'Label, SN remote',      cnLabel: '遥控器 SN 标',         enLabel: 'Label, SN remote',      mainNode: '09', renderType: 'enum' },
  { field: 'Label, Battery SN',     cnLabel: '电池 SN 标',           enLabel: 'Label, Battery SN',     mainNode: '09', renderType: 'enum' },
  { field: 'Label, Inspection Sheet', cnLabel: '检验单标',            enLabel: 'Label, Inspection Sheet', mainNode: '09', renderType: 'enum' },
  { field: 'Label, Shipping box',   cnLabel: '出货箱标',             enLabel: 'Label, Shipping box',   mainNode: '09', renderType: 'enum' },

  // ─── 节点 11 PDI 检测 / 包装齐套 ───
  /* 节点 11 v3 主写：业务方在 PDI 检测时填。优先 metadata 手填，fallback snapshot 派生。
     注：snapshot 由 backend 事件流派生，metadata 由 PUT /:id 直接写；
     后续 backend 加 snapshot update endpoint 后可统一走 snapshot 路径。 */
  { field: 'Specialist',            cnLabel: '负责 Specialist',      enLabel: 'Specialist',            mainNode: '11',
    accessor: (r) => g(r, 'metadata', 'specialist') ?? g(r, 'snapshot', 'currentSpecialist', 'name'),
    renderType: 'text', writePath: { kind: 'metadata', key: 'specialist' } },
  { field: 'Physical Product Status', cnLabel: '物理状态',            enLabel: 'Physical Product Status', mainNode: '11',
    accessor: (r) => g(r, 'metadata', 'physicalProductStatus') ?? g(r, 'snapshot', 'physicalProductStatus'),
    renderType: 'enum', writePath: { kind: 'metadata', key: 'physicalProductStatus' },
    enumOptions: [
      { value: 'STORED', label: '在库' },
      { value: 'IN_TRANSIT', label: '运输中' },
      { value: 'DELIVERED', label: '已交付' },
      { value: 'IN_SERVICE', label: '服役中' },
      { value: 'RETIRED', label: '已退役' },
    ] },
  { field: 'Days Ready for Delivery', cnLabel: '可交付天数',          enLabel: 'Days Ready for Delivery', mainNode: '11',
    accessor: (r) => g(r, 'metadata', 'daysReadyForDelivery') ?? g(r, 'snapshot', 'daysReadyForDelivery'),
    renderType: 'number', writePath: { kind: 'metadata', key: 'daysReadyForDelivery' } },
  { field: 'Robot',                 cnLabel: '机器人主机',           enLabel: 'Robot',                 mainNode: '11', renderType: 'enum' },
  { field: 'Battery',               cnLabel: '电池',                 enLabel: 'Battery',               mainNode: '11', renderType: 'enum' },
  { field: 'Remote',                cnLabel: '遥控器',               enLabel: 'Remote',                mainNode: '11', renderType: 'enum' },
  { field: 'USA Power cable',       cnLabel: '美式电源线',           enLabel: 'USA Power cable',       mainNode: '11', renderType: 'enum' },
  { field: 'CN cable w adapter',    cnLabel: '中式带转接线',         enLabel: 'CN cable w adapter',    mainNode: '11', renderType: 'enum' },
  { field: 'Power supply',          cnLabel: '电源',                 enLabel: 'Power supply',          mainNode: '11', renderType: 'enum' },
  { field: 'Charging dock',         cnLabel: '充电底座',             enLabel: 'Charging dock',         mainNode: '11', renderType: 'enum' },
  { field: 'foot pads',             cnLabel: '脚垫',                 enLabel: 'foot pads',             mainNode: '11', renderType: 'enum' },
  { field: 'tools/ spare kit',      cnLabel: '工具/备件包',          enLabel: 'tools/ spare kit',      mainNode: '11', renderType: 'enum' },
  { field: 'paperwork',             cnLabel: '文件资料',             enLabel: 'paperwork',             mainNode: '11', renderType: 'enum' },

  // ─── 节点 12 锁单 / 合同 ───
  { field: 'Customer',              cnLabel: '客户',                 enLabel: 'Customer',              mainNode: '12', accessor: (r) => g(r, 'customer', 'name') ?? g(r, 'snapshot', 'currentCustomer', 'name'), renderType: 'text', defaultVisible: true },
  { field: 'Sales Order ID',        cnLabel: '销售订单号',           enLabel: 'Sales Order ID',        mainNode: '12', accessor: (r) => g(r, 'snapshot', 'currentSalesOrder', 'code'), renderType: 'text' },
  { field: 'Sales Price - Hardware', cnLabel: '销售价-硬件',         enLabel: 'Sales Price - Hardware', mainNode: '12', renderType: 'money' },
  { field: 'Sales Price - Software', cnLabel: '销售价-软件',         enLabel: 'Sales Price - Software', mainNode: '12', renderType: 'money' },
  { field: 'Mentor',                cnLabel: '导师(Mentor)',         enLabel: 'Mentor',                mainNode: '12', renderType: 'text' },
  { field: 'Mentee',                cnLabel: '学员(Mentee)',         enLabel: 'Mentee',                mainNode: '12', renderType: 'text' },
  { field: 'Contract Status',       cnLabel: '合同状态',             enLabel: 'Contract Status',       mainNode: '12', renderType: 'enum',
    enumOptions: [{ value: 'Signed', label: '已签 (Signed)' }] /* v5 实际取值 */ },
  { field: 'Robotics Sales Agreement Link', cnLabel: '机器人销售协议链接', enLabel: 'Robotics Sales Agreement Link', mainNode: '12', renderType: 'url' },
  { field: 'Superone Cocreation Contract', cnLabel: '共创合约 Superone', enLabel: 'Superone Cocreation Contract', mainNode: '12', renderType: 'url' },
  { field: 'Robotic Cocreation Contract',  cnLabel: '机器人共创合约',   enLabel: 'Robotic Cocreation Contract',  mainNode: '12', renderType: 'url' },

  // ─── 节点 13 交付前合同 ───
  { field: 'Payment & Contract (PreDel)', cnLabel: '交付前付款合同状态', enLabel: 'Payment & Contract (PreDel)', mainNode: '13', renderType: 'enum',
    enumOptions: [
      { value: 'Confirmed Ready For Delivery', label: '已确认可交付' },
      { value: 'Not Paid Yet', label: '尚未付款' },
      { value: 'Not Confirmed', label: '未确认' },
    ] /* v5 实际取值 */ },

  // ─── 节点 14 准备交付 ───
  { field: 'Delivery Number',       cnLabel: '交付单号',             enLabel: 'Delivery Number',       mainNode: '14', renderType: 'text' },
  { field: 'Delivery Request Type', cnLabel: '交付方式',             enLabel: 'Delivery Request Type', mainNode: '14', renderType: 'enum',
    enumOptions: [
      { value: 'Customer Pick-up (Will Call)', label: '客户自提' },
      { value: '3PL Delivery - U.S. & Territories', label: '3PL 配送 (美国)' },
      { value: 'FF White Glove Delivery', label: 'FF 白手套配送' },
    ] /* v5 实际取值 */ },
  { field: 'Expected Delivery Date', cnLabel: '预计交付日期',         enLabel: 'Expected Delivery Date', mainNode: '14', renderType: 'date' },

  // ─── 节点 15 已交付 ───
  { field: 'Delivery Date',         cnLabel: '交付时间',             enLabel: 'Delivery Date',         mainNode: '15', renderType: 'datetime' as any },
  { field: 'Cost',                  cnLabel: '成本',                 enLabel: 'Cost',                  mainNode: '15', renderType: 'money' },
  { field: 'Gross Margin',          cnLabel: '毛利',                 enLabel: 'Gross Margin',          mainNode: '15', renderType: 'money' },
  { field: 'Revenue Recognition',   cnLabel: '收入确认',             enLabel: 'Revenue Recognition',   mainNode: '15', renderType: 'enum' },
  { field: 'Invoice Status',        cnLabel: '开票状态',             enLabel: 'Invoice Status',        mainNode: '15', renderType: 'enum' },
  { field: 'Warranty Status',       cnLabel: '保修状态',             enLabel: 'Warranty Status',       mainNode: '15',
    /* v3 主写：优先 metadata 手填，fallback snapshot 派生 */
    accessor: (r) => g(r, 'metadata', 'warrantyStatus') ?? g(r, 'snapshot', 'warrantyStatus'),
    renderType: 'enum', writePath: { kind: 'metadata', key: 'warrantyStatus' },
    enumOptions: [
      { value: 'INACTIVE', label: '未激活' },
      { value: 'ACTIVE', label: '保修中' },
      { value: 'EXPIRED', label: '已过期' },
      { value: 'VOIDED', label: '已作废' },
    ] },

  // ─── DA 交付审批 / 验收 ───
  { field: 'Delivery Signed Form',  cnLabel: '签字表状态',           enLabel: 'Delivery Signed Form',  mainNode: 'DA', renderType: 'enum',
    enumOptions: [
      { value: 'Preparation in Progress', label: '准备中' },
      { value: 'Sent - Pending Signature', label: '已发送 待签字' },
    ] /* v5 实际取值 */ },
  { field: 'Delivery Acceptance Form (Pre-Del)', cnLabel: '验收表',  enLabel: 'Delivery Acceptance Form (Pre-Del)', mainNode: 'DA', renderType: 'enum' },
  { field: 'Delivery Acceptance Form Status (Sales)', cnLabel: '验收表状态(Sales)', enLabel: 'Delivery Acceptance Form Status (Sales)', mainNode: 'DA', renderType: 'enum',
    enumOptions: [
      { value: 'Ready to Send (Judy Wu)', label: '待发送 (Judy Wu)' },
      { value: 'Complete', label: '完成' },
    ] /* v5 实际取值 */ },
  { field: 'Deliver Acception Form Hyperlink', cnLabel: '验收表链接', enLabel: 'Deliver Acception Form Hyperlink', mainNode: 'DA', renderType: 'url' },
  { field: 'Note (Pre-Del)',        cnLabel: '交付前备注',           enLabel: 'Note (Pre-Del)',        mainNode: 'DA', renderType: 'text' },

  // ─── P6.1 收款 ───
  { field: 'Payment Method',        cnLabel: '付款方式',             enLabel: 'Payment Method',        mainNode: 'P6.1', renderType: 'enum',
    enumOptions: [
      { value: 'Deposit Transfer', label: '定金转账' },
      { value: 'Check', label: '支票' },
      { value: 'ACH', label: 'ACH' },
      { value: 'Lease', label: '租赁' },
      { value: 'Full Payment - Check', label: '全款 支票' },
      { value: 'Full Payment - Wire', label: '全款 电汇' },
      { value: 'Co-Creation Credit', label: '共创信用' },
      { value: 'transfer', label: '转账' },
    ] /* v5 实际取值 */ },
  { field: 'Payment',               cnLabel: '收款状态',             enLabel: 'Payment',               mainNode: 'P6.1', renderType: 'enum',
    enumOptions: [
      { value: 'Paid', label: '已付' },
      { value: 'Not Paid Yet', label: '尚未付款' },
      { value: 'Paid in April', label: '4 月已付' },
      { value: 'Canceled', label: '已取消' },
      { value: 'Draft', label: '草稿' },
    ] /* v5 实际取值 */ },
  { field: 'Deposit Transfer Form Link', cnLabel: '定金转账单链接',  enLabel: 'Deposit Transfer Form Link', mainNode: 'P6.1', renderType: 'url' },
  { field: 'Deposit Transfer Contract Link - Car', cnLabel: '定金转账合约链接 汽车', enLabel: 'Deposit Transfer Contract Link - Car', mainNode: 'P6.1', renderType: 'url' },

  // ─── ST 售后 ───
  { field: 'Service Records',       cnLabel: '服务记录',             enLabel: 'Service Records',       mainNode: 'ST', renderType: 'text' },
  { field: 'Customer Feedback',     cnLabel: '客户反馈',             enLabel: 'Customer Feedback',     mainNode: 'ST', renderType: 'text' },

  // ─── multi 跨节点更新 ───
  { field: 'Status (Build)',        cnLabel: '状态(Build)',          enLabel: 'Status (Build)',        mainNode: 'multi', accessor: (r) => g(r, 'snapshot', 'currentStage'),    renderType: 'badge' /* read-only：snapshot 派生 */ },
  { field: 'Lifecycle Status',      cnLabel: '生命周期状态',         enLabel: 'Lifecycle Status',      mainNode: 'multi', accessor: (r) => g(r, 'snapshot', 'currentStage'),    renderType: 'badge', defaultVisible: true },
  { field: 'Location',              cnLabel: '当前位置',             enLabel: 'Location',              mainNode: 'multi', accessor: (r) => g(r, 'location', 'name') ?? g(r, 'snapshot', 'currentLocation', 'name'), renderType: 'text', defaultVisible: true },
  { field: 'Delivery Status',       cnLabel: '交付状态',             enLabel: 'Delivery Status',       mainNode: 'multi', renderType: 'enum',
    enumOptions: [
      { value: 'Delivered', label: '已交付' },
      { value: 'Staged', label: '已暂存' },
      { value: 'Delivered (D2-Returned(After Sales))', label: '已交付（D2 售后退回）' },
      { value: 'Robot Not Ready', label: '机器人未就绪' },
      { value: 'Shipping', label: '运输中' },
      { value: 'Ready for Delivery - Acceptance Form Created', label: '可交付 验收表已建' },
    ] /* v5 实际取值（去掉 dirty value "Robot Ready 04/29"）*/ },
  { field: 'Notes / Comments',      cnLabel: '备注',                 enLabel: 'Notes / Comments',      mainNode: 'multi', renderType: 'text' },

  // ─── GLOBAL 治理 ───
  { field: 'Sources',               cnLabel: '数据来源',             enLabel: 'Sources',               mainNode: 'GLOBAL', renderType: 'text' },
  { field: 'Conflict Count',        cnLabel: '字段冲突计数',         enLabel: 'Conflict Count',        mainNode: 'GLOBAL', renderType: 'text' },
  { field: 'Record Status',         cnLabel: '记录状态',             enLabel: 'Record Status',         mainNode: 'GLOBAL', accessor: (r) => (g(r, 'deletedAt') ? '已删除' : '正常'), renderType: 'text' },
];

/** 节点分组定义 — UI 折叠分组时用 */
export interface NodeGroupDef {
  /** mainNode key */
  node: string;
  /** 显示名（中文） */
  cnLabel: string;
  /** 显示名（英文） */
  enLabel: string;
  /** 关联的 lifecycle stage（mainNode 对应"当前 stage 时该 group 是活跃的"） */
  activeStages: RobotLifecycleStage[];
}

export const NODE_GROUPS: NodeGroupDef[] = [
  { node: '01',     cnLabel: '采购下单',     enLabel: 'PO Created',        activeStages: ['SUPPLY_PO_CREATED'] },
  { node: '03',     cnLabel: '工厂出货 (G1/G2)', enLabel: 'Factory Ship', activeStages: ['SUPPLY_READY_TO_SHIP'] /* IN_PRODUCTION 阶段不填 Date ready/sticker */ },
  { node: '04',     cnLabel: '国际运输',     enLabel: 'Logistics',         activeStages: ['LOGISTICS_IN_TRANSIT'] },
  { node: '05',     cnLabel: '保税',         enLabel: 'Bonded',            activeStages: ['LOGISTICS_BONDED'] },
  { node: '06',     cnLabel: '清关 (G3)',    enLabel: 'Customs',           activeStages: ['LOGISTICS_CUSTOMS_CLEARED'] },
  { node: '07',     cnLabel: '美国收货 / 激活', enLabel: 'Receive + Activate', activeStages: ['WAREHOUSE_RECEIVED'] },
  { node: '08',     cnLabel: 'W1 改装前初检', enLabel: 'W1 Inspection',     activeStages: ['WAREHOUSE_AT_W1_PDI'] },
  { node: '09',     cnLabel: '标签贴附',     enLabel: 'Label Apply',       activeStages: ['WAREHOUSE_MODIFICATION'] /* 贴标在改装阶段，W1_PDI 阶段只填 Issue/Issue Tag */ },
  { node: '11',     cnLabel: 'PDI 检测 / 齐套', enLabel: 'PDI / Bundle',   activeStages: ['WAREHOUSE_BRANDED_READY'] /* 齐套在 BRANDED_READY 阶段填（业务方权威 stage-fields.ts）*/ },
  { node: '12',     cnLabel: '锁单 (G4)',    enLabel: 'Sales Reserved',    activeStages: ['SALES_RESERVED'] },
  { node: '13',     cnLabel: '交付前合同',   enLabel: 'Pre-Del Contract',  activeStages: ['SALES_PAYMENT_VALIDATED'] /* 业务方权威：Payment & Contract PreDel 在 VALIDATED 后填 */ },
  { node: '14',     cnLabel: '准备交付',     enLabel: 'Prepare Delivery',  activeStages: ['DELIVERY_READY'] /* APPROVAL 阶段填 DA 节点的验收表字段，不重填 14 */ },
  { node: '15',     cnLabel: '已交付 (G6)',  enLabel: 'Delivered',         activeStages: ['DELIVERY_DELIVERED'] },
  { node: 'DA',     cnLabel: '交付审批 / 验收', enLabel: 'Delivery Acceptance', activeStages: ['DELIVERY_APPROVAL'] },
  { node: 'P6.1',   cnLabel: '收款 (G5)',    enLabel: 'Payment',           activeStages: ['DELIVERY_PAYMENT_COLLECTED'] /* 业务方权威：VALIDATED 不再填收款字段 */ },
  { node: 'ST',     cnLabel: '售后',         enLabel: 'After-sales',       activeStages: ['AFTERSALES_TICKET'] /* 业务方权威：仅 TICKET 阶段填 Service Records/Customer Feedback */ },
  { node: 'multi',  cnLabel: '跨节点',       enLabel: 'Multi-node',        activeStages: [] /* 任何 stage 都活跃 */ },
  { node: 'GLOBAL', cnLabel: '治理',         enLabel: 'Governance',        activeStages: [] },
];

export function getV3FieldsByNode(node: string): V3FieldDef[] {
  return V3_FIELDS.filter((f) => f.mainNode === node);
}

export function getV3FieldByKey(key: string): V3FieldDef | undefined {
  return V3_FIELDS.find((f) => f.field === key);
}

/** 取字段值 — 优先 def.accessor，否则 fallback 到 robot.metadata[normalizeFieldKey(field)] */
export function getFieldValue(robot: RobotUnit, def: V3FieldDef): any {
  if (def.accessor) {
    const v = def.accessor(robot);
    if (v !== undefined) return v;
  }
  const meta = (robot as any).metadata ?? {};
  return meta[normalizeFieldKey(def.field)];
}

/** 取字段写路径 — 优先 def.writePath，否则 fallback 到 metadata 默认路径 */
export function getFieldWritePath(def: V3FieldDef): WritePath | null {
  if (def.writePath) return def.writePath;
  // 显式标 read-only 的字段（已落 column 但来自关联表，不应在此面板改）
  // 通过有 accessor 且无 writePath 判断：这些是 PO/Model/SKU 等关联表派生字段
  if (def.accessor) return null;
  // 其他未配置字段：默认走 metadata，key 用 normalize
  return { kind: 'metadata', key: normalizeFieldKey(def.field) };
}

export function formatV3Value(def: V3FieldDef, raw: any): string {
  if (raw == null || raw === '') return '—';
  switch (def.renderType) {
    case 'date':
      try {
        return new Date(raw).toLocaleDateString();
      } catch {
        return String(raw);
      }
    case 'money':
      return typeof raw === 'number' ? raw.toLocaleString() : String(raw);
    default:
      return String(raw);
  }
}
